import { keepPreviousData, skipToken, useInfiniteQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { BoardItemView } from "../../../entities/board/model/use-boards";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type BoardTimelineRange = {
  endDate: string;
  startDate: string;
};

type TimelinePage = Awaited<ReturnType<typeof trpc.boards.readTimelinePage.query>>;
type TimelineQueryData = InfiniteData<TimelinePage>;
type TimelineFieldValue = { fieldDefinitionId: string; value: string | string[] };

export function useBoardTimelineItems({
  boardId,
  boardViewId,
  dateFieldId,
  includeClosedItems,
  queryKey,
  range,
  sprintScope = null,
}: {
  boardId: string;
  boardViewId: string;
  dateFieldId: string | null;
  includeClosedItems: boolean;
  queryKey?: string;
  range: BoardTimelineRange | null;
  sprintScope?: string | null;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const previousQueryKeyRef = useRef(queryKey);
  const timelineQueryKey = useMemo(() => trpc.boards.readTimelinePage.infiniteQueryKey(), [trpc]);
  const queryRange = useMemo(() => dateFieldId && range ? timelineQueryRange(range) : null, [dateFieldId, range]);
  // The view options travel with the request (and the query key), so a
  // refetch racing a concurrent view-settings mutation still returns data
  // that matches what the user just toggled.
  const queryOptions = useMemo(() => trpc.boards.readTimelinePage.infiniteQueryOptions(
    queryRange && dateFieldId ? {
      boardId,
      boardViewId,
      dateFieldId,
      endDate: queryRange.endDate,
      includeClosedItems,
      limit: 200,
      sprintScope,
      startDate: queryRange.startDate,
    } : skipToken,
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
    },
  ), [boardId, boardViewId, dateFieldId, includeClosedItems, queryRange, sprintScope, trpc]);
  const query = useInfiniteQuery(queryOptions);

  useEffect(() => {
    if (previousQueryKeyRef.current === queryKey) return;
    previousQueryKeyRef.current = queryKey;
    void queryClient.invalidateQueries({ queryKey: timelineQueryKey });
  }, [queryClient, queryKey, timelineQueryKey]);

  useEffect(() => {
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    void query.fetchNextPage();
  }, [query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage]);

  const items = useMemo(() => {
    const itemById = new Map<string, BoardItemView>();
    for (const page of query.data?.pages ?? []) {
      for (const item of page.items) itemById.set(item.id, item);
    }
    return [...itemById.values()];
  }, [query.data]);

  function applyItemFieldValuesOptimistic(workObjectId: string, values: TimelineFieldValue[]) {
    const previous = findTimelineItem(queryClient, timelineQueryKey, workObjectId);
    updateTimelineQueries(queryClient, timelineQueryKey, workObjectId, (item) => updateItemFieldValues(item, values));

    return () => {
      if (!previous) return;
      updateTimelineQueries(queryClient, timelineQueryKey, workObjectId, (item) => {
        const stillOptimistic = values.every((value) => {
          const currentValue = item.fieldValues.find((fieldValue) => fieldValue.fieldDefinitionId === value.fieldDefinitionId)?.value;
          return fieldValueEquals(currentValue, value.value);
        });
        return stillOptimistic ? { ...item, fieldValues: previous.fieldValues } : item;
      });
    };
  }

  // Vertical swimlane moves change the status; patching the cache up front
  // lets the bar land in its new section the moment the drag ends instead of
  // waiting out the refetch.
  function applyItemStatusOptimistic(workObjectId: string, status: Partial<BoardItemView["status"]>) {
    const previous = findTimelineItem(queryClient, timelineQueryKey, workObjectId);
    updateTimelineQueries(queryClient, timelineQueryKey, workObjectId, (item) => ({ ...item, status: { ...item.status, ...status } }));

    return () => {
      if (!previous) return;
      updateTimelineQueries(queryClient, timelineQueryKey, workObjectId, (item) => ({ ...item, status: previous.status }));
    };
  }

  // Items created outside the timeline query cache (drag-to-create) only
  // appear after a refetch.
  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: timelineQueryKey }),
    [queryClient, timelineQueryKey],
  );

  return {
    applyItemFieldValuesOptimistic,
    applyItemStatusOptimistic,
    error: query.error instanceof Error ? query.error.message : query.error ? String(query.error) : null,
    invalidate,
    // With `skipToken` (no date field yet) the query stays pending but never
    // fetches — that must not read as an endless loading state.
    isLoading: (query.isPending && query.isFetching) || query.isFetchingNextPage,
    items,
  };
}

function updateTimelineQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  timelineQueryKey: readonly unknown[],
  workObjectId: string,
  updateItem: (item: BoardItemView) => BoardItemView,
) {
  queryClient.setQueriesData<TimelineQueryData>({ queryKey: timelineQueryKey }, (data) => {
    if (!data) return data;
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: page.items.map((item) => item.workObject.id === workObjectId ? updateItem(item) : item),
      })),
    };
  });
}

function findTimelineItem(
  queryClient: ReturnType<typeof useQueryClient>,
  timelineQueryKey: readonly unknown[],
  workObjectId: string,
) {
  const queryData = queryClient.getQueriesData<TimelineQueryData>({ queryKey: timelineQueryKey });
  for (const [, data] of queryData) {
    for (const page of data?.pages ?? []) {
      const item = page.items.find((candidate) => candidate.workObject.id === workObjectId);
      if (item) return item;
    }
  }
  return null;
}

function updateItemFieldValues(item: BoardItemView, values: TimelineFieldValue[]) {
  const fieldValues = item.fieldValues.map((fieldValue) => {
    const patch = values.find((value) => value.fieldDefinitionId === fieldValue.fieldDefinitionId);
    return patch ? { ...fieldValue, value: patch.value } : fieldValue;
  });
  for (const value of values) {
    if (!fieldValues.some((fieldValue) => fieldValue.fieldDefinitionId === value.fieldDefinitionId)) {
      fieldValues.push({ fieldDefinitionId: value.fieldDefinitionId, value: value.value });
    }
  }
  return { ...item, fieldValues };
}

function timelineQueryRange(range: BoardTimelineRange) {
  const startDate = addTimelineDays(timelineBucketStart(range.startDate), -TIMELINE_QUERY_OVERLAP_DAYS);
  const endDate = addTimelineDays(timelineBucketEnd(range.endDate), TIMELINE_QUERY_OVERLAP_DAYS);
  return { endDate, startDate };
}

const TIMELINE_QUERY_BUCKET_DAYS = 42;
const TIMELINE_QUERY_OVERLAP_DAYS = 14;

function timelineBucketStart(value: string) {
  const epochDays = daysBetween("1970-01-01", value);
  return addTimelineDays(value, -(epochDays % TIMELINE_QUERY_BUCKET_DAYS));
}

function timelineBucketEnd(value: string) {
  return addTimelineDays(timelineBucketStart(value), TIMELINE_QUERY_BUCKET_DAYS - 1);
}

function daysBetween(first: string, second: string) {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((Date.parse(`${second}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / millisecondsPerDay);
}

function addTimelineDays(value: string, amount: number) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function fieldValueEquals(first: string | string[] | undefined, second: string | string[]) {
  if (Array.isArray(first) && Array.isArray(second)) return first.length === second.length && first.every((value, index) => value === second[index]);
  return first === second;
}
